<script>
import axios from 'axios';
import Card from './Card';
import Icons from './Icons';
import Badges from './Badges';
import Input from '../../../components/base/Input';

/**
 * To reset checkout on close
 */
const reset = {
  account: {
    name: '',
    email: '',
    document: '',
  },

  credit_card: {
    name: '',
    number: '',
    validity: '',
    security_code: '',
  },
};

/**
 * Array of priority of errors
 */
const priorityErrors = ['number', 'expiration', 'verification_value'];

export default {
  components: {
    Card,
    Icons,
    Input,
    Badges,
  },

  props: {
    checkout: {
      type: Object,
      required: true,
    },

    publicToken: {
      type: String,
      required: true,
    },

    test: {
      type: String,
      required: false,
      default: '',
    },
  },

  data: () => ({
    show: null,
    error: null,
    bankslip: '',
    loading: null,
    touched: false,
    oneclick: false,
    installments: 1,
    paymentStatus: '',
    actionContent: null,
    method: 'creditcard',
    bankslipContent: null,
    path: 'https://api.bulldesk.com.br/checkout',
    account: {
      name: '',
      email: '',
      document: '',
    },
    credit_card: {
      name: '',
      number: '',
      validity: '',
      security_code: '',
    },
    cardErrors: {
      //
    },
    accountErrors: {
      //
    },
  }),

  created () {
    this.verifyOneclick();
  },

  computed: {
    /**
     * Get error of card based on prioritys
     */
    cardError() {
      const { cardErrors } = this;

      const error = priorityErrors.find((priority) => cardErrors[priority]);

      return cardErrors[error];
    },

    /**
     * Get brand of card
     */
    cardBrand() {
      return Iugu.utils.getBrandByCreditCardNumber(this.credit_card.number);
    },

    price() {
      /**
       * Todo: diff locales
       */
      const formatter = new Intl.NumberFormat('pt-br', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      });

      return formatter.format(this.checkout.price);
    },

    description() {
      const { name } = this.checkout;

      if (name) {
        return name;
      }

      return null;
    },
  },

  methods: {
    /**
     * Verify if session has token
     */
    verifyOneclick () {
      /**
       * Get "token" of session storage
       */
      const token = this.getSessionStorage('oneclick');

      if (! token || this.hasExpired(token) || ! this.checkout.one_click) {
        return;
      }

      this.oneclick = token.value;
    },

    getSessionStorage (key) {
      /**
       * Get 'bdk-checkout-token' of the session storage
       */
      const settings = JSON.parse(window.sessionStorage.getItem('bdk-checkout-token')) || {};

      /**
       * Return object of key
       */
      return settings[key];
    },

    /**
     * Set value on session storage
     */
    setSessionStorage (flag) {
      const expiry = this.getExpiryDate();

      const value = {
        oneclick: {
          expiry,
          value: flag,
        },
      };

      window.sessionStorage.setItem('bdk-checkout-token', JSON.stringify(value));

      this.oneclick = flag;
    },

    clearSessionStorage () {
      /**
       * Remove value of session storage
       */
      window.sessionStorage.removeItem('bdk-checkout-token');

      this.oneclick = null;
    },

    getExpiryDate () {
      const now = new Date().getTime();

      /**
       * 1 hour: 60 seconds * 60 (minute);
       */
      return now * 60 * 60;
    },

    /**
     * Verify if date of value has expired;
     */
    hasExpired ({ expiry = 0 }) {
      const now = new Date();

      return now.getTime() > expiry;
    },

    generalPayment() {
      const payload = {
        token: this.publicToken,
        checkout: this.checkout.hashid,
      };

      if (this.oneclick) {
        this.send({
          ...payload,
          'session_token': this.oneclick,
        });

        return;
      }

      this.send({
        ...payload,
        ...this.account,
        installments: this.installments,
      });
    },

    creditCardPayment() {
      /**
       * Return if has error on card
       */
      if (this.cardError && this.cardError !== 'is_invalid') {
        return;
      }

      this.clearErrors();

      Iugu.setAccountID(window.BulldeskSettings.iuguAccountId);

      Iugu.setup();

      if (this.test) {
        Iugu.setTestMode(true);
      }

      const names = this.credit_card.name.split(' ');

      const [first = '', second = ''] =
        this.credit_card.validity.replace(/\D/g, '').match(/.{1,2}/g) || '';

      /**
       * Get a iugu token of card
       */
      const cc = Iugu.CreditCard(
        this.credit_card.number,
        first,
        second,
        names[0],
        names[1],
        this.credit_card.security_code,
      );

      /**
       * Callback for 'create-payment-token'
       */
      const tokenResponseHandler = (data) => {
        if (data.errors) {
          this.cardErrors = data.errors || {};

          this.loading = null;

          return;
        }

        const payload = {
          iugu_token: data.id,
          token: this.publicToken,
          checkout: this.checkout.hashid,
          installments: this.installments,
          ...this.account,
        };

        this.send(payload);
      };

      try {
        Iugu.createPaymentToken(cc, tokenResponseHandler);
      } catch (e) {
        Bugsnag.notify(e);

        this.loading = null;
      }

      return false;
    },

    callback({ checkout, payment: { url, methods, status, session_token } }) {
      this.bankslip = url;
      this.paymentStatus = status;

      if (this.checkout.one_click) {
        this.setSessionStorage(session_token);
      }

      this.actionContent = checkout.action === 'message'
        ? checkout.compiled_action_content
        : null;

      if (!this.actionContent && !methods.bankslip && checkout.action_content) {
        this.redirectTo(checkout.action_content);

        return;
      }

      this.show = true;
    },

    redirectTo (url) {
      window.location.href = url;
    },

    validate(value) {
      if (value.length < 15) {
        return;
      }

      const code = this.credit_card.security_code;

      if (code.length === 3) {
        const verificationValue = !Iugu.utils.validateCVV(code, this.cardBrand)
          ? 'CVC do cartão inválido.'
          : null;

        this.cardErrors = {
          ...this.cardErrors,
          verification_value: verificationValue,
        };
      }
    },

    clearErrors() {
      /**
       * reset errors
       */
      this.error = null;
      this.cardErrors = {};
      this.accountErrors = {};
    },

    clearInputs() {
      /**
       * reset inputs
       */
      this.account = { ...reset.account };
      this.credit_card = { ...reset.credit_card };
    },

    close() {
      this.clearErrors();
      this.clearInputs();
      this.$emit('close');
    },

    /**
     * Submit
     */
    submit() {
      this.loading = 'Processando...';

      if (this.method === 'creditcard' && ! this.oneclick) {
        this.creditCardPayment();

        return;
      }

      this.generalPayment();
    },

    send(payload) {
      axios
        .post(this.test || this.path, payload)
        .then(({ data }) => {
          this.touched = true;

          this.loading = 'Sucesso! Resgatando...';

          this.callback(data);
        })
        .catch(({ response: { data } }) => {
          this.touched = true;

          this.loading = null;

          const { errors, message } = data;

          if (!errors && !message) {
            this.accountErrors = data;

            return;
          }

          const error = errors ? errors[0] : message;

          this.error = error;

          Bugsnag.notify(error);

          this.clearSessionStorage();
        })
        .finally(() => {
          if (this.touched) {
            this.touched = false;

            return;
          }

          this.loading = null;
        });
    },
  },

  watch: {
    /**
     * Watch on value of card number input and validate this
     */
    'credit_card.number': {
      handler(value) {
        if (value.length === 19) {
          const parsed = value.replace(/\D/g, '');

          const number = !Iugu.utils.validateCreditCardNumber(parsed)
            ? 'Número do cartão inválido.'
            : null;

          this.cardErrors = { ...this.cardErrors, number };

          return;
        }

        this.cardErrors.number = null;
      },

      immediate: false,
    },

    /**
     * Watch on value of card validity input and validate this
     */
    'credit_card.validity': {
      handler(value) {
        if (value.length === 7) {
          const parsed = value
            .replace(/\D/g, '')
            .match(/.{1,2}/g)
            .join('/');

          const expiration = !Iugu.utils.validateExpirationString(parsed)
            ? 'Validade do cartão inválida.'
            : null;

          this.cardErrors = { ...this.cardErrors, expiration };

          return;
        }
        this.cardErrors.expiration = null;
      },

      immediate: false,
    },

    /**
     * Watch on value of card VCC input and validate this
     */
    'credit_card.security_code': {
      handler(value) {
        if (value.length === 3) {
          const verificationValue = !Iugu.utils.validateCVV(
            value,
            this.cardBrand,
          )
            ? 'CVC do cartão inválido.'
            : null;

          this.cardErrors = {
            ...this.cardErrors,
            verification_value: verificationValue,
          };

          return;
        }
        this.cardErrors.verification_value = null;
      },

      immediate: false,
    },

    /**
     * Watch on value of card VCC input and validate this
     */
    'credit_card.name': {
      handler() {
        if (this.cardErrors.last_name || this.cardErrors.first_name) {
          this.cardErrors.last_name = null;
          this.cardErrors.first_name = null;
        }
      },

      immediate: false,
    },
  },
};
</script>

<template>
  <form
    class="bdk-checkout bdk-preflight bdk-z-10"
    @submit.prevent="submit"
    :class="{
      'is-inline': checkout.inline,
      'bdk-shadow-none': oneclick && checkout.inline,
    }"
  >
    <div
      :class="[
        checkout.inline ? 'bdk-w-full' : 'bdk-w-96',
        oneclick ? ! checkout.inline ? 'bdk-bg-white' : 'bdk-bg-transparent' : 'bdk-bg-white',
      ]"
      class="bdk-flex bdk-flex-col bdk-justify-start bdk-rounded-lg bdk-relative bdk-p-6"
    >
      <button
        @click.prevent="close"
        v-if="!checkout.inline"
        class="bdk-absolute bdk-flex bdk-items-center bdk-right-6 bdk-top-6"
      >
        <svg
          class="bdk-h-6 bdk-w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <template
        v-if="show"
      >
        <div
          v-html="actionContent"
          v-if="actionContent && paymentStatus !== 'pending'"
        />

        <span
          v-else
          class="bdk-text-center bdk-flex bdk-flex-col bdk-items-center bdk-justify-items-center"
        >
          <template
            v-if="paymentStatus === 'pending'"
          >
            <span
              v-text="__('Você receberá o boleto no seu e-mail')"
            />

            <button
              v-if="bankslip"
              @click.prevent="redirectTo(bankslip)"
              :class="[ ! checkout.inline ? 'bdk-bg-indigo-600 hover:bdk-bg-indigo-700' : 'button' ]"
              class="bdk-inline-flex bdk-justify-center bdk-items-center bdk-px-6 bdk-py-3 bdk-border bdk-border-transparent bdk-rounded-md bdk-shadow-sm bdk-mt-4 bdk-w-max"
            >
              <span
                v-text="__('Acessar Boleto')"
                class="bdk-text-base bdk-font-medium"
                :class="{ 'bdk-text-white': ! checkout.inline }"
              />
            </button>
          </template>

          <span
            v-else
            v-text="__('Pagamento Aprovado!')"
          />
        </span>
      </template>

      <template
        v-else
      >
        <template
          v-if="! oneclick"
        >
          <span
            v-text="__('Dados do Comprador')"
            class="bdk-block bdk-text-lg bdk-font-medium bdk-text-gray-700 bdk-text-left "
          />

          <Input
            type="text"
            label="Nome"
            class="bdk-mt-4"
            :required="true"
            v-model="account.name"
            :error="accountErrors.name ? accountErrors.name[0] : null"
          />

          <Input
            type="email"
            label="E-mail"
            class="bdk-mt-4"
            :required="true"
            v-model="account.email"
            :error="accountErrors.email ? accountErrors.email[0] : null"
          />

          <Input
            label="CPF/CNPJ"
            :required="true"
            class="bdk-mt-4"
            v-model="account.document"
            :mask="['###.###.###-##', '##.###.###/####-##']"
            :error="accountErrors.document ? accountErrors.document[0] : null"
          />
        </template>

        <template
          v-if="! oneclick || ! checkout.inline"
        >
          <span
            v-text="__('Dados de pagamento')"
            class="bdk-block bdk-text-lg bdk-font-medium bdk-text-gray-700 bdk-text-left bdk-mt-4"
          />

          <span
            v-text="`Preço: ${price}`"
            class="bdk-text-left bdk-mt-2 bdk-text-sm bdk-font-medium bdk-text-gray-700 bdk-text-left"
          />

          <span
            v-text="`Descrição: ${description}`"
            class="bdk-text-left bdk-mt-1 bdk-text-sm bdk-font-medium bdk-text-gray-700 bdk-text-left"
          />
        </template>

        <template
          v-if="! oneclick"
        >
          <span
            v-text="__('Formas de Pagamento')"
            class="bdk-block bdk-text-lg bdk-font-medium bdk-text-gray-700 bdk-text-left bdk-mt-4"
          />

          <div class="bdk-flex bdk-spacing-x-2 bdk-mt-2">
            <div
              @click.prevent="method = 'creditcard'"
              :class="{ 'bdk-border-indigo-500': method === 'creditcard' }"
              class="bdk-relative bdk-border bdk-rounded-lg bdk-shadow-sm bdk-p-2 bdk-flex bdk-items-center bdk-cursor-pointer focus:bdk-outline-none bdk-flex-1 bdk-border-gray-300"
            >
              {{ checkout.inline ? __('Cartão de Crédito') : __('Cartão') }}

              <Icons icon="creditcard" class="bdk-ml-auto" />
            </div>

            <div
              @click.prevent="method = 'bankslip'"
              :class="{ 'bdk-border-indigo-500': method === 'bankslip' }"
              class="bdk-relative bdk-bg-white bdk-border bdk-rounded-lg bdk-ml-2 bdk-items-center bdk-shadow-sm bdk-p-2 bdk-flex bdk-cursor-pointer focus:bdk-outline-none bdk-flex-1 bdk-border-gray-300"
            >
              {{ checkout.inline ? __('Boleto Bancário') : __('Boleto') }}

              <Icons class="bdk-ml-auto" />
            </div>

            <div
              @click.prevent="method = 'pix'"
              :class="{ 'bdk-border-indigo-500': method === 'pix' }"
              class="bdk-relative bdk-bg-white bdk-border bdk-rounded-lg bdk-ml-2 bdk-items-center bdk-shadow-sm bdk-p-2 bdk-flex bdk-cursor-pointer focus:bdk-outline-none bdk-flex-1 bdk-border-gray-300"
            >
              Pix

              <Icons icon="pix" class="bdk-ml-auto" />
            </div>
          </div>

          <Card
            class="bdk-mt-4"
            :card-error="cardError"
            :price="checkout.price"
            :name.sync="credit_card.name"
            v-if="method === 'creditcard'"
            :installment.sync="installments"
            :number.sync="credit_card.number"
            :validity.sync="credit_card.validity"
            :code.sync="credit_card.security_code"
            :installments="checkout.installment_options"
          />
        </template>

        <div
          class="bdk-mt-4"
          :class="{ 'bdk-mt-4': ! oneclick || ! checkout.inline }"
        >
          <button
            v-if="loading"
            @click.prevent.stop
            :class="[ ! checkout.inline ? 'bdk-bg-indigo-600 hover:bdk-bg-indigo-700' : 'button' ]"
            class="bdk-w-full bdk-inline-flex bdk-justify-center bdk-items-center bdk-px-6 bdk-py-3 bdk-border bdk-border-transparent bdk-rounded-md bdk-shadow-sm"
          >
            <span
              v-text="loading"
              class="bdk-text-base bdk-font-medium"
              :class="{ 'bdk-text-white': ! checkout.inline }"
            />

            <div
              :class="[ ! checkout.inline ? 'bdk-border-l-white' : 'bdk-border-l-current' ]"
              class="bdk-w-4 bdk-h-4 bdk-ml-2 bdk-animate-spin bdk-rounded-full bdk-border-2 bdk-border-transparent"
            />
          </button>

          <button
            v-else
            :disabled="loading"
            v-text="checkout.button"
            :class="{ 'bdk-bg-indigo-600 hover:bdk-bg-indigo-700 focus:bdk-ring-indigo-500 bdk-text-white': ! checkout.inline }"
            class="bdk-w-full bdk-inline-flex bdk-justify-center bdk-items-center bdk-px-6 bdk-py-3 bdk-border bdk-border-transparent bdk-text-base bdk-font-medium bdk-rounded-md bdk-shadow-sm focus:bdk-outline-none focus:bdk-ring-2 focus:bdk-ring-offset-2"
          />
        </div>

        <template
          v-if="! oneclick || ! checkout.inline"
        >
          <span
            v-if="error"
            v-text="error"
            class="bdk-mt-2 bdk-text-sm bdk-text-red-600"
          />

          <div class="bdk-flex bdk-px-2 bdk-py-2 bdk-justify-center">
            <Badges variant="primary" />
          </div>

          <div
            class="bdk-text-xs bdk-text-xs bdk-text-gray-500 bdk-font-medium bdk-text-center"
          >
            {{ __('Bulldesk está processando este pedido à serviço de') }}

            <span class="bdk-uppercase" v-text="checkout.tenant.company_name" />

            <span
              class="bdk--ml-1"
              v-text="__(', ao continuar, você concorda com os')"
            />

            <a
              v-text="__('Termos de Uso')"
              href="https://salesflowcrm.movidesk.com/kb/article/405957/termos-de-uso"
              class="bdk-text-xs bdk-text-xs bdk-text-indigo-800 bdk-font-medium"
            />
          </div>
        </template>
      </template>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.bdk-checkout {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  display: flex;
  z-index: 99999;
  position: fixed;
  align-items: center;
  align-content: center;
  justify-content: center;
  background-color: rgba(black, 0.7);
  box-shadow: 0 5px 20px rgba(165, 131, 224, 0.15);
  font-family: 'BlinkMacSystemFont', 'Popins', sans-serif;

  &.is-inline {
    position: relative !important;
    background-color: transparent !important;
  }
}
</style>
